import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { todayApi } from "api/today";
import { approvalsApi } from "api/approvals";
import { tasksApi } from "api/tasks";

function greetingFor(dateUtc) {
  const hour = new Date(dateUtc).getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function timeLabel(dateUtc) {
  return new Date(dateUtc).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function StatCard({ icon, label, value, hint, tone }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} size={16} color={tone || "var(--color-primary)"} />
        <span className="font-caption text-xs uppercase tracking-wide" style={{ color: "var(--color-text-secondary)" }}>
          {label}
        </span>
      </div>
      <p className="font-heading font-bold text-2xl" style={{ color: "var(--color-foreground)" }}>
        {value}
      </p>
      {hint && (
        <p className="font-caption text-[11px] mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * Today (Sprint 6, M14 — Part 86): the rep's daily workflow in one view —
 * hours saved, approvals to decide, tasks due today/overdue and suggested
 * calls. One-tap actions resolve items inline; nothing happens autonomously.
 */
export default function Today() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await todayApi.get();
      setData(data);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load your day.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    setBusyId(id);
    setNotice("");
    try {
      await approvalsApi.approve(id);
      setNotice("Approval applied.");
      await load();
    } catch (error) {
      setLoadError(error?.message || "Could not approve.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    setBusyId(id);
    setNotice("");
    try {
      await approvalsApi.reject(id);
      setNotice("Approval dismissed.");
      await load();
    } catch (error) {
      setLoadError(error?.message || "Could not reject.");
    } finally {
      setBusyId(null);
    }
  };

  const handleTaskDone = async (id) => {
    setBusyId(id);
    setNotice("");
    try {
      await tasksApi.update(id, { status: "done" });
      setNotice("Task completed.");
      await load();
    } catch (error) {
      setLoadError(error?.message || "Could not complete the task.");
    } finally {
      setBusyId(null);
    }
  };

  const tasksOverdue = (data?.tasksToday || []).filter(
    (t) => t.status !== "done" && t.dueUtc && new Date(t.dueUtc) < new Date(data.dateUtc).setUTCHours(0, 0, 0, 0),
  ).length;

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
            {data ? `${greetingFor(data.dateUtc)}!` : "Today"}
          </h1>
          <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            {data ? timeLabel(data.dateUtc) : "Your daily workflow"} · Approve and act — nothing happens on its own
          </p>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
          </div>
        )}
        {notice && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-success)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-success)" }}>{notice}</p>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading your day...</p>
          </div>
        ) : !data ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="mx-auto mb-2" />
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
              We could not load your day. Check your connection and try again.
            </p>
            <Button variant="default" size="sm" className="mt-4" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={load}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard icon="Clock" label="Hours saved today" value={`${data.hoursSavedToday ?? 0} hr`} hint={`${data.aiEventsToday ?? 0} AI-assisted actions`} />
              <StatCard icon="ShieldCheck" label="Approvals waiting" value={data.pendingApprovals?.length ?? 0} hint="Your call — approve or dismiss" />
              <StatCard icon="CheckSquare" label="Tasks due today" value={data.tasksToday?.length ?? 0} hint={tasksOverdue ? `${tasksOverdue} overdue` : "All on schedule"} tone={tasksOverdue ? "var(--color-error)" : "var(--color-primary)"} />
              <StatCard icon="PhoneCall" label="Suggested calls" value={data.suggestedCalls?.length ?? 0} hint="Follow-up drafts + call tasks" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Suggested calls */}
              <section>
                <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>
                  Suggested calls
                </h2>
                {data.suggestedCalls?.length === 0 ? (
                  <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                    <Icon name="PhoneCall" size={20} color="var(--color-text-secondary)" className="mx-auto mb-2" />
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      No suggested calls yet. Follow-up drafts for idle threads will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                    {data.suggestedCalls.map((call) => (
                      <div key={`${call.type}-${call.id}`} className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                            {call.title}
                          </p>
                          <p className="font-caption text-[11px] mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                            {call.type === "follow_up_draft"
                              ? `Follow-up draft · ${Math.round((call.confidence || 0) * 100)}% confidence`
                              : `Call task · due ${call.dueUtc ? new Date(call.dueUtc).toLocaleString("en-IN") : "anytime"}`}
                          </p>
                        </div>
                        {call.type === "call" ? (
                          <Button variant="outline" size="sm" iconName="Check" iconPosition="left" iconSize={14} loading={busyId === call.id} onClick={() => handleTaskDone(call.id)}>
                            Done
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" iconName="MessageSquare" iconPosition="left" iconSize={14} onClick={() => navigate("/inbox")}>
                            Open thread
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Approvals */}
              <section>
                <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>
                  Approvals
                </h2>
                {data.pendingApprovals?.length === 0 ? (
                  <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                    <Icon name="ShieldCheck" size={20} color="var(--color-text-secondary)" className="mx-auto mb-2" />
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      Nothing waiting. AI suggestions and follow-up drafts land here for your approval.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                    {data.pendingApprovals.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                            {item.field === "follow_up_draft"
                              ? item.suggestedValue?.content || "Follow-up draft"
                              : `Suggested ${item.field.split(".").pop()}`}
                          </p>
                          <p className="font-caption text-[11px] mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                            Confidence {Math.round((item.confidence || 0) * 100)}% · {item.sourceRef || "—"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button variant="default" size="sm" iconName="Check" iconPosition="left" iconSize={14} loading={busyId === item.id} onClick={() => handleApprove(item.id)}>
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" iconName="X" iconPosition="left" iconSize={14} onClick={() => handleReject(item.id)}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Tasks */}
            <section className="mt-6">
              <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>
                Tasks due today
              </h2>
              {data.tasksToday?.length === 0 ? (
                <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                  <Icon name="CheckSquare" size={20} color="var(--color-text-secondary)" className="mx-auto mb-2" />
                  <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    All clear for today. Follow-up tasks land here as they are created.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                  {data.tasksToday.map((task) => {
                    const overdue = task.dueUtc && new Date(task.dueUtc) < new Date(data.dateUtc).setUTCHours(0, 0, 0, 0);
                    return (
                      <div key={task.id} className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="min-w-0">
                          <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                            {task.title}
                          </p>
                          <p className="font-caption text-[11px] mt-0.5" style={{ color: overdue ? "var(--color-error)" : "var(--color-text-secondary)" }}>
                            {overdue ? "Overdue" : task.dueUtc ? `Due ${new Date(task.dueUtc).toLocaleString("en-IN")}` : "No due time"} · {task.type || "follow_up"}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" iconName="Check" iconPosition="left" iconSize={14} loading={busyId === task.id} onClick={() => handleTaskDone(task.id)}>
                          Mark done
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-6 border-t" style={{ borderColor: "var(--color-border)" }}>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            &copy; {new Date().getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            Approve to send — the CRM never acts on its own
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
